import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Sidebar } from 'primeng/primeng';
import { SelectItem } from 'primeng/components/common/selectitem';

import { TagService } from '../../../../services/tag.service';
import { StepComponent } from '../../../step.component';
import { StrtInitiative } from '../../../../../shared/models/plan/StrtInitiative';
import { StrtObjective } from '../../../../../shared/models/plan/StrtObjective';
import {InitiativeService} from '../../../../services/initiative.service';
import {take} from 'rxjs/internal/operators';
import {Observable} from 'rxjs/index';
import {AppLoadingService} from '../../../../../app-loading-panel/app-loading.service';

@Component({
  selector: 'app-initiative',
  templateUrl: './initiative.component.html',
  styleUrls: ['./initiative.component.scss']
})
export class InitiativeComponent implements OnInit, OnChanges {
  readonly MAX_DESCRIPTION_LENGTH = 200;

  @Output() create = new EventEmitter<StrtInitiative>();
  @Output() createError = new EventEmitter<Error>();
  @Output() edit = new EventEmitter<StrtInitiative>();
  @Output() editError = new EventEmitter<Error>();
  @Output() cancel = new EventEmitter<void>();

  @Input() strategyId: number;

  private _initiativeForm: FormGroup;
  private _isActive = false;
  private _isEdit = false;
  private pristineInitiative: StrtInitiative;
  private pristineObjective: StrtObjective;

  tagSelectItems: SelectItem[] = [{label: 'Select a Tag', value: undefined}];

  isLoading$: Observable<boolean>;

  constructor(private formBuilder: FormBuilder,
              private tagService: TagService,
              private _initiativeService: InitiativeService,
              private _loadingService: AppLoadingService) { }

  ngOnInit() {
    this.isLoading$ = this._loadingService.isLoading$;
    this.createForm();
    this.tagService.getAdEDTags().subscribe(it => {
      this.tagSelectItems.push(...this.tagService.getTagSelectItems(it));
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    StepComponent.logNgOnChanges(changes);
  }

  createInitiative(objective: StrtObjective) {
    this._isActive = true;
    this._isEdit = false;
    this.pristineInitiative = undefined;
    this.pristineObjective = objective;
  }

  editInitiative(objective: StrtObjective, initiative: StrtInitiative) {
    this._isActive = true;
    this._isEdit = true;
    this.pristineObjective = objective;
    this.pristineInitiative = initiative;
    this.initiativeForm.patchValue({
      description: this.pristineInitiative.description,
      tag: this.pristineInitiative.tagId
    });
  }

  get initiativeForm() {
    return this._initiativeForm;
  }

  get description() {
    return this.initiativeForm.get('description');
  }

  get tag() {
    return this.initiativeForm.get('tag');
  }

  get headerContent() {
    return this._isEdit ? 'Edit Critical Initiative' : 'Add Critical Initiative';
  }

  get objectiveText() {
    return this.pristineObjective ? this.pristineObjective.text : '';
  }

  get isSubmitDisabled() {
    return !(this.initiativeForm.status === 'VALID' &&
      !this.initiativeForm.pristine &&
      this.initiativeForm.dirty);
  }

  get descriptionRemainingCharacters() {
    return this.MAX_DESCRIPTION_LENGTH - this.description.value.length;
  }

  get isActive() {
    return this._isActive;
  }

  set isActive(isActive) {
    this._isActive = isActive;
  }

  get isEdit() {
    return this._isEdit;
  }

  onShowSidebar(sidebar: Sidebar) {
    sidebar.maskClickListener();
  }

  onHideSidebar() {
    this.initiativeForm.reset({description: '', tag: undefined});
    this._isEdit = false;
    this.pristineInitiative = undefined;
    this.pristineObjective = undefined;
  }

  onSubmit() {
    const initiative = this.formToInitiative();
    if (this._isEdit) {
      this.onInitiativeEdit(initiative);
    } else {
      this.onInitiativeCreate(initiative);
    }
  }

  private createForm() {
    this._initiativeForm = this.formBuilder.group({
      description: ['', [Validators.maxLength(this.MAX_DESCRIPTION_LENGTH), Validators.required]],
      tag: ['', [Validators.required]]
    });
  }

  private formToInitiative(): StrtInitiative {
    return {
      strategyId: this.pristineObjective.strategyId,
      objectiveId: this.pristineObjective.objectiveId,
      initiativeId: this.pristineInitiative ? this.pristineInitiative.initiativeId : undefined,
      description: this.description.value,
      tagId: this.tag.value,
      outcomes: this.pristineInitiative ? this.pristineInitiative.outcomes : []
    };
  }

  onInitiativeCreate(initiative: StrtInitiative) {
    this._loadingService.setLoading();
    initiative.strategyId = this.strategyId;
    this._initiativeService.createInitiative(initiative).pipe(take(1)).subscribe((result: any) => {
      this.create.emit(result);
      this.isActive = false;
    }, (err) => {
      this._loadingService.clearLoading();
      console.error(`Failed to create initiative: `, err);
    });
  }

  onInitiativeEdit(initiative: StrtInitiative) {
    this._loadingService.setLoading();
    initiative.strategyId = this.strategyId;
    this._initiativeService.updateInitiative(initiative).pipe(take(1)).subscribe((result: any) => {
      this.edit.emit(result);
      this.isActive = false;
    }, (err) => {
      this._loadingService.clearLoading();
      console.error(`Failed to edit initiative: `, err);
    });
  }

}
